

































































































































import Vue from 'vue';
import ItemsProvider, { CashFilter, ISearchParams } from '@/api/ItemsProvider';
import IItemStub from '@/models/items/IItemStub';
import ItemSearchRankSelector from './ItemSearchRankSelector.vue';
import ItemSearchCategorySelector from './ItemSearchCategorySelector.vue';
import ItemSearchPartSelector from './ItemSearchPartSelector.vue';
import ItemSearchTypeSelector from './ItemSearchTypeSelector.vue';
import DvBrowseFrame from '@/components/util/DvBrowseFrame.vue';
import DvStagger from '@/components/util/DvStagger.vue';
import Loader from '@/components/util/Loader.vue';
import Item from '@/old/api/item/item';
import {
    IBrowseFilter, SortKey, SortDir, Density,
    emptyFilter, filterToQuery, queryToFilter, defaultDir,
} from '@/util/browseQuery';
import { IFilterChip, ChipLabelSource, buildChips } from '@/util/filterChips';
import { ITEM_RANKS } from '@/models/items/itemRanks';

const PAGE_SIZE = 100;
const MIN_LENGTH = 2;

//  Shares ITEM_RANKS with ItemSearchRankSelector.vue (its checkbox list) --
//  see that module for why this is NOT sourced from a numeric `ItemRank`
//  enum, which was stale and mis-numbered relative to the search API's
//  actual `g=` encoding. These used to be two hand-kept-in-sync copies.
const RANK_LABELS: Record<number, string> =
    ITEM_RANKS.reduce((acc, r) => { acc[r.id] = r.label; return acc; }, {} as Record<number, string>);

//  Mirrors DvBrowseFrame.vue's own (unimported) BrowseState/ISortOption --
//  every other .vue-to-.vue import in this codebase pulls only the default
//  export, so these small shapes are duplicated locally rather than betting
//  on named-export resolution through the *.vue ambient shim.
type BrowseState = 'prompt' | 'loading' | 'results' | 'empty' | 'error' | 'end';

interface ISortOption {
    key: SortKey;
    label: string;
}

interface Parent { label: string; childIds: number[]; }

interface IData {
    filter: IBrowseFilter;
    items: IItemStub[];
    estimatedSize: number;
    state: BrowseState;
    errorDetail: string;
    loadSeq: number;
    loadedOffset: number;
    loadingMore: boolean;
    //  Split point for the stagger: everything before it is the first
    //  successfully-loaded page (staggered in); everything after is an
    //  appended page (rendered flat). Null until the first page resolves.
    firstPageCount: number | null;
    categoryParents: Parent[];
    partParents: Parent[];
    typeParents: Parent[];
    categoryLabels: Record<number, string>;
    partLabels: Record<number, string>;
    typeLabels: Record<number, string>;
    //  Canonical serialization of the last query WE pushed, so the $route
    //  watcher below can tell its own echo apart from a genuine external
    //  navigation (back/forward, a pasted link) and avoid re-searching twice.
    lastPushedQuery: string;
}

function sameIdSet(a: number[], b: number[]): boolean {
    if (a.length !== b.length) {
        return false;
    }
    const s = new Set(a);
    return b.every((x) => s.has(x));
}

//  Mirrors DvItemCard's own `name` computed exactly -- table density reads
//  the same IItemStub fields the cards do, just through a plain function
//  instead of a per-card computed property.
function itemDisplayName(item: IItemStub): string {
    const n = item.name;
    if (n?.name && n._NameID) return n.name;
    if (n?._NameID) return `Item MID: ${n._NameID}`;
    return `Unnamed Item ${item.id}`;
}

function canonicalQuery(q: Record<string, any>): string {
    return Object.keys(q)
        .filter((k) => q[k] !== undefined && q[k] !== null && q[k] !== '')
        .sort()
        .map((k) => `${k}=${q[k]}`)
        .join('&');
}

export default Vue.extend({
    components: {
        ItemSearchRankSelector,
        ItemSearchCategorySelector,
        ItemSearchPartSelector,
        ItemSearchTypeSelector,
        DvBrowseFrame,
        DvStagger,
        Loader,
    },
    data(): IData {
        return {
            filter: emptyFilter(),
            items: [],
            estimatedSize: 0,
            state: 'prompt',
            errorDetail: '',
            loadSeq: 0,
            loadedOffset: 0,
            loadingMore: false,
            firstPageCount: null,
            categoryParents: [],
            partParents: [],
            typeParents: [],
            categoryLabels: {},
            partLabels: {},
            typeLabels: {},
            lastPushedQuery: '',
        };
    },
    computed: {
        minLength(): number {
            return MIN_LENGTH;
        },
        hasQuery(): boolean {
            return this.filter.name.trim().length >= MIN_LENGTH;
        },
        sorts(): ISortOption[] {
            return [
                { key: 'relevance', label: 'Relevance' },
                { key: 'name', label: 'Name' },
                { key: 'id', label: 'ID' },
            ];
        },
        firstPageItems(): IItemStub[] {
            return this.firstPageCount != null ? this.items.slice(0, this.firstPageCount) : this.items;
        },
        restItems(): IItemStub[] {
            return this.firstPageCount != null ? this.items.slice(this.firstPageCount) : [];
        },
        chips(): IFilterChip[] {
            const labels: ChipLabelSource = {
                rank: (id) => RANK_LABELS[id] ?? `Rank ${id}`,
                category: (id) => this.categoryLabels[id] ?? `Category ${id}`,
                part: (id) => this.partLabels[id] ?? `Part ${id}`,
                type: (id) => this.typeLabels[id] ?? `Type ${id}`,
                categoryParents: this.categoryParents,
                partParents: this.partParents,
                typeParents: this.typeParents,
            };
            return buildChips(this.filter, labels);
        },
        hasMore(): boolean {
            return this.loadedOffset < this.estimatedSize;
        },
    },
    //  Seeded in created(), not mounted(): Vue mounts CHILDREN before the
    //  parent's own mounted() hook fires, and the four filter selectors
    //  (rank/category/part/type) each sync themselves from $route on mount
    //  too. A selector whose own mount-time sync runs synchronously (rank,
    //  part, type -- category is naturally delayed behind its own async
    //  tree fetch) and finds a non-empty value to restore emits @select,
    //  which pushes a URL built from THIS component's filter -- and if that
    //  happens before this hook has seeded filter.name from the incoming
    //  URL, the push carries name: '' and permanently drops the name= query
    //  param from a URL that combined a name search with e.g. a non-empty
    //  rank filter. Verified live: `?name=sword&g=5,7` lost `name=sword`
    //  entirely when seeding happened in mounted(); created() runs before
    //  any child is even instantiated, closing the race. See
    //  task-7-report.md fix round 3.
    created() {
        this.filter = queryToFilter(this.$route.query as Record<string, string | string[] | undefined>);
        this.lastPushedQuery = canonicalQuery(filterToQuery(this.filter));
        this.search(true);
    },
    watch: {
        //  Reacts to genuine external navigation (back/forward, a pasted link)
        //  without double-searching on our own router.replace calls below --
        //  those are recognised by comparing against lastPushedQuery.
        $route(to) {
            const incoming = canonicalQuery(to.query as Record<string, any>);
            if (incoming === this.lastPushedQuery) {
                return;
            }
            this.filter = queryToFilter(to.query as Record<string, string | string[] | undefined>);
            this.lastPushedQuery = incoming;
            this.search(true);
        },
    },
    methods: {
        //  Table density's own two cell renderers -- exposed as methods (not
        //  computed) since each takes the row's item, not `this`.
        itemDisplayName,
        itemCategory(item: IItemStub): string {
            return Item.getItemDetailedCategory(item);
        },
        pushUrl() {
            const q = filterToQuery(this.filter);
            this.lastPushedQuery = canonicalQuery(q);
            this.$router.replace({ ...this.$route, query: q });
        },
        //  Trimmed here (matching the pre-Task-7 page's v-model.trim), not
        //  merely at the request-building call site: this keeps filter.name,
        //  the URL, and the outgoing request all consistent, and pushes the
        //  trimmed value back down as the `value` prop, which the frame's own
        //  echo-guard then uses to visually strip the leading/trailing space
        //  from the search box too. hasQuery already trims for the minLength
        //  check, so a whitespace-only value (e.g. "  ") trims to '' here and
        //  correctly falls below minLength -- it does not sneak past as a
        //  "2-character" query.
        onNameInput(name: string) {
            this.filter.name = name.trim();
            this.pushUrl();
            this.search(true);
        },
        onLevelChange() {
            //  v-model.number yields '' (not NaN) for a cleared box, and
            //  isNaN('') is false, so a plain isNaN() check lets an empty
            //  level input reach the request as an empty param. Check the
            //  runtime type first; filter.minLevel/maxLevel are typed number
            //  but the DOM can hand back a string here.
            if (typeof this.filter.minLevel !== 'number' || isNaN(this.filter.minLevel)) {
                this.filter.minLevel = 0;
            }
            if (typeof this.filter.maxLevel !== 'number' || isNaN(this.filter.maxLevel)) {
                this.filter.maxLevel = 100;
            }
            if (this.filter.minLevel > this.filter.maxLevel) {
                this.filter.maxLevel = this.filter.minLevel;
            }
            this.pushUrl();
            this.search(true);
        },
        onSort(key: SortKey) {
            this.filter.sort = key;
            this.filter.dir = defaultDir(key);
            this.pushUrl();
            this.search(true);
        },
        onDir(dir: SortDir) {
            this.filter.dir = dir;
            this.pushUrl();
            this.search(true);
        },
        onDensity(density: Density) {
            this.filter.density = density;
            this.pushUrl();
        },
        //  Ranks are a flat list (no parent to collapse into -- buildChips
        //  hardcodes [] for the 'ranks' group's parents), and their labels
        //  come from the static RANK_LABELS map above, not from reading the
        //  selector's own tree via $refs -- so, unlike category/part/type,
        //  there is no *Parents/*Labels refresh to do here.
        onRankSelect(ids: number[]) {
            if (sameIdSet(this.filter.ranks, ids)) {
                return;
            }
            this.filter.ranks = ids;
            this.pushUrl();
            this.search(true);
        },
        onCategorySelect(ids: number[]) {
            this.refreshCategoryParents();
            if (sameIdSet(this.filter.categories, ids)) {
                return;
            }
            this.filter.categories = ids;
            this.pushUrl();
            this.search(true);
        },
        onPartSelect(ids: number[]) {
            this.refreshPartParents();
            if (sameIdSet(this.filter.parts, ids)) {
                return;
            }
            this.filter.parts = ids;
            this.pushUrl();
            this.search(true);
        },
        onTypeSelect(ids: number[]) {
            this.refreshTypeParents();
            if (sameIdSet(this.filter.types, ids)) {
                return;
            }
            this.filter.types = ids;
            this.pushUrl();
            this.search(true);
        },
        //  Chip removal dispatches on chip.group, not chip.removeIds: the level
        //  chip carries removeIds: [] (a range isn't a selection), so a generic
        //  remove-by-ids handler would make its X do nothing.
        onRemoveChip(chip: IFilterChip) {
            if (chip.group === 'level') {
                this.filter.minLevel = 0;
                this.filter.maxLevel = 100;
            } else {
                const remove = new Set(chip.removeIds);
                this.filter[chip.group] = this.filter[chip.group].filter((id) => !remove.has(id));
            }
            this.pushUrl();
            this.search(true);
        },
        onClearChips() {
            const f = this.filter;
            f.minLevel = 0;
            f.maxLevel = 100;
            f.ranks = [];
            f.categories = [];
            f.parts = [];
            f.types = [];
            this.pushUrl();
            this.search(true);
        },
        onRetry() {
            this.search(true);
        },
        onLoadMore() {
            if (this.loadingMore || this.state !== 'results') {
                return;
            }
            this.search(false);
        },
        //  You own building the three *Parents arrays -- from the selector
        //  components' own resolved trees via $refs, since their `@select`
        //  contract carries only ids, not the labelled shape chip-building
        //  needs. Refreshed on every @select (not a computed off $refs: a
        //  computed that first evaluates before a child mounts never
        //  establishes a dependency on that child's data and would go stale
        //  forever).
        refreshCategoryParents() {
            const ref = this.$refs.categorySelector as any;
            const cats = (ref && ref.categories) || [];
            const parents: Parent[] = [];
            const labels: Record<number, string> = {};
            for (const c of cats) {
                const childIds = (c.types || []).map((t: any) => t.id);
                parents.push({ label: c.label, childIds });
                for (const t of c.types || []) {
                    labels[t.id] = t.label;
                }
            }
            this.categoryParents = parents;
            this.categoryLabels = labels;
        },
        refreshPartParents() {
            const ref = this.$refs.partSelector as any;
            const f = ref && ref.filters;
            const parents: Parent[] = [];
            const labels: Record<number, string> = {};
            if (f) {
                const nonCashIds = f.nonCash.options.map((o: any) => o.id);
                f.nonCash.options.forEach((o: any, i: number) => { labels[nonCashIds[i]] = o.label; });
                parents.push({ label: 'Normal', childIds: nonCashIds });

                const cashIds = f.cash.options.map((o: any) => -(o.id + 1));
                f.cash.options.forEach((o: any, i: number) => { labels[cashIds[i]] = o.label; });
                parents.push({ label: 'Cash', childIds: cashIds });

                const weaponIds = f.weapons.options.map((o: any) => o.id + 100);
                f.weapons.options.forEach((o: any, i: number) => { labels[weaponIds[i]] = o.label; });
                parents.push({ label: 'Weapons', childIds: weaponIds });
            }
            this.partParents = parents;
            this.partLabels = labels;
        },
        refreshTypeParents() {
            const ref = this.$refs.typeSelector as any;
            const entries = (ref && ref.entries) || [];
            const labels: Record<number, string> = {};
            for (const e of entries) {
                labels[e.id] = e.label;
            }
            //  Flat list: no grouping, so no parent ever collapses a set of
            //  types into one chip. buildChips/collapse handles [] correctly.
            this.typeParents = [];
            this.typeLabels = labels;
        },
        async search(reset: boolean) {
            if (!this.hasQuery) {
                this.loadSeq++;
                this.state = 'prompt';
                this.items = [];
                this.estimatedSize = 0;
                this.loadedOffset = 0;
                this.firstPageCount = null;
                //  This early return bypasses the finally block below (an
                //  append in flight when the query drops below minLength
                //  never reaches it), so this branch must clear its own
                //  loadingMore rather than relying on that finally.
                this.loadingMore = false;
                return;
            }

            const seq = reset ? ++this.loadSeq : this.loadSeq;
            if (reset) {
                this.state = 'loading';
                this.items = [];
                this.loadedOffset = 0;
                this.firstPageCount = null;
                this.errorDetail = '';
            } else {
                if (!this.hasMore) {
                    return;
                }
                this.loadingMore = true;
            }

            try {
                //  A bare numeric query is a direct item id, not a name.
                //  Digits-only rather than Number()/isNaN(): the latter also
                //  accepts "12.5", "-5" and "1e5", none of which is ever an
                //  item id, and it would swallow a legitimate name search for
                //  such a string. This matches QuickNav's own /^\d+$/ test.
                //  (SkillSearch keeps its looser pre-existing check; changing
                //  that is out of scope here.)
                //  Only on a fresh search (reset): a point lookup has no
                //  further pages, so this never runs as a load-more append.
                const trimmed = this.filter.name.trim();
                const asId = Number(trimmed);
                if (reset && /^\d+$/.test(trimmed)) {
                    const stub = await ItemsProvider.getStub(asId);
                    if (seq !== this.loadSeq) {
                        return;
                    }
                    this.items = stub ? [stub] : [];
                    this.estimatedSize = this.items.length;
                    this.loadedOffset = this.items.length;
                    this.firstPageCount = this.items.length;
                    this.state = this.items.length === 0 ? 'empty' : (this.hasMore ? 'results' : 'end');
                    return;
                }

                const params: ISearchParams = {
                    name: this.filter.name,
                    ranks: this.filter.ranks,
                    types: this.filter.types,
                    parts: this.filter.parts,
                    cash: CashFilter.ALL,
                    categories: this.filter.categories,
                    classes: [],
                    minLevel: this.filter.minLevel,
                    maxLevel: this.filter.maxLevel,
                    page: Math.floor(this.loadedOffset / PAGE_SIZE),
                    pageSize: PAGE_SIZE,
                    sort: this.filter.sort,
                    dir: this.filter.dir,
                };
                const res = await ItemsProvider.search(params);
                if (seq !== this.loadSeq) {
                    return;
                }

                //  Dedupe by item id; track the offset by raw fetched count (not
                //  deduped length) so pagination never re-requests a window it
                //  already consumed.
                const have = new Set(this.items.map((i) => i.id));
                const fresh = res.items.filter((i) => {
                    if (have.has(i.id)) {
                        return false;
                    }
                    have.add(i.id);
                    return true;
                });
                this.items = this.items.concat(fresh);
                this.estimatedSize = res.estimatedSize;
                this.loadedOffset += res.items.length;
                if (reset) {
                    this.firstPageCount = this.items.length;
                }
                if (!res.items.length) {
                    //  Defensive: an empty page with hasMore still true would
                    //  otherwise chain-load forever.
                    this.estimatedSize = this.loadedOffset;
                }

                this.state = this.items.length === 0 ? 'empty' : (this.hasMore ? 'results' : 'end');
                this.$nextTick(() => this.maybeChainLoad());
            } catch (e) {
                if (seq !== this.loadSeq) {
                    return;
                }
                this.errorDetail = String(e);
                this.state = 'error';
            } finally {
                if (seq === this.loadSeq) {
                    this.loadingMore = false;
                }
            }
        },
        //  Continued chain-loading is ours, not the frame's: it only re-checks
        //  the sentinel on the initial transition INTO 'results' (a state
        //  watcher), which doesn't fire again on results -> results. After
        //  every append we re-measure the frame's own sentinel (reached via its
        //  exposed $refs -- there's exactly one, always mounted, per
        //  CashShopBrowser's fix) fresh with getBoundingClientRect(), never a
        //  cached IntersectionObserver flag, which can go stale when a
        //  cache-served append outruns the callback. This used to also wait out
        //  an rAF-gated DvFade crossfade around the results branch, but that
        //  wrapper was removed from DvBrowseFrame (fix round 2 -- mode="out-in"
        //  on the whole state switch could strand a rendered-but-still-fading
        //  results grid at opacity:0 forever in any environment that throttles
        //  or starves requestAnimationFrame). The branch is now a plain
        //  v-if/v-else-if, patched synchronously, so $nextTick alone is enough.
        maybeChainLoad() {
            if (this.state !== 'results' || this.loadingMore) {
                return;
            }
            const frame = this.$refs.frame as any;
            const el = frame && frame.$refs && (frame.$refs.sentinel as HTMLElement | undefined);
            if (el && el.getBoundingClientRect().top < window.innerHeight + 600) {
                this.search(false);
            }
        },
    },
});
